import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  TemplateRef,
  Type,
  ViewEncapsulation,
} from '@angular/core';
import {CommonModule, NgComponentOutlet} from '@angular/common';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {DIALOG_DATA, DIALOG_REF, DialogRef} from '../model/dialog.model';

export interface DialogWrapperData {
  componentType: Type<unknown>;
  componentData: unknown;
  dialogRef: DialogRef;
  headerTemplate?: TemplateRef<unknown> | Type<unknown>;
  footerTemplate?: TemplateRef<unknown> | Type<unknown>;
}

@Component({
  selector: 'app-dynamic-dialog-wrapper',
  imports: [CommonModule, NgComponentOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './dialog.component.html',
})
export class DynamicDialogWrapperComponent {
  private readonly primengRef = inject(DynamicDialogRef);
  private readonly primengConfig = inject(DynamicDialogConfig);
  private readonly parentInjector = inject(Injector);

  private readonly wrapperData = computed<DialogWrapperData>(
    () => this.primengConfig.data as DialogWrapperData
  );

  readonly bodyComponent = computed(() => this.wrapperData().componentType);

  readonly headerTemplate = computed<TemplateRef<unknown> | Type<unknown> | undefined>(
    () => this.wrapperData().headerTemplate
  );

  readonly footerTemplate = computed<TemplateRef<unknown> | Type<unknown> | undefined>(
    () => this.wrapperData().footerTemplate
  );

  readonly headerComponent = computed<Type<unknown> | null>(() => {
    const h = this.headerTemplate();
    return h && this.isComponentType(h) ? (h as Type<unknown>) : null;
  });

  readonly footerComponent = computed<Type<unknown> | null>(() => {
    const f = this.footerTemplate();
    return f && this.isComponentType(f) ? (f as Type<unknown>) : null;
  });

  readonly childInjector = computed(() => {
    const data = this.wrapperData();

    data.dialogRef._closeCallback = (result?: unknown) => {
      data.dialogRef._resolve(result);
      this.primengRef.close(result);
    };

    return Injector.create({
      parent: this.parentInjector,
      providers: [
        {provide: DIALOG_DATA, useValue: data.componentData},
        {provide: DIALOG_REF, useValue: data.dialogRef},
      ],
    });
  });

  readonly templateCtx = computed(() => ({
    $implicit: this.wrapperData().dialogRef,
  }));

  asTemplate(t: unknown): TemplateRef<unknown> {
    return t as TemplateRef<unknown>;
  }

  private isComponentType(value: unknown): boolean {
    return typeof value === 'function' && !(value instanceof TemplateRef);
  }
}
